import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { searchOrders } from '../../utils/strapiOrders';

// Import RTK Query APIs for listening to cache invalidations
import { orderApi } from '../apis/endpoints/orderApi';

const initialState = {
  // Search and filter state
  searchTerm: '',
  sortBy: 'orders',
  filters: {
    status: 'all', // all, ongoing, completed, canceled
    roleTab: 'all', // all, buying, selling, renting, lending
    orderType: 'all', // all, purchase, rental, service, mixed
    dateFrom: null,
    dateTo: null,
    minAmount: null,
    maxAmount: null,
    category: '',
    brand: ''
  },

  // Results state
  loading: false,
  error: null,
  orders: [],
  totalResults: 0,
  currentPage: 0,
  totalPages: 0,
  hasMore: false,

  // UI state
  isInitialized: false,
  searchSuggestions: [],
  suggestionsLoading: false,

  // Filter counts for UI badges (optional)
  filterCounts: {
    ongoing: 0,
    completed: 0,
    canceled: 0,
    buying: 0,
    selling: 0,
    renting: 0,
    lending: 0
  }
};

// Parse URL parameters for transactions
const parseTransactionUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  const searchTerm = params.get('search') || '';
  const sortBy = params.get('sort') || 'orders';

  const filters = { ...initialState.filters };

  // Parse filter parameters
  const roleTab = params.get('role') || 'all';
  const status = params.get('status') || 'all';
  const orderType = params.get('type') || 'all';
  const dateFrom = params.get('from');
  const dateTo = params.get('to');
  const minAmount = params.get('minAmount');
  const maxAmount = params.get('maxAmount');
  const category = params.get('category') || '';
  const brand = params.get('brand') || '';

  // Validate status parameter
  const validStatuses = ['all', 'ongoing', 'completed', 'canceled'];
  const validRoleTabs = ['all', 'buying', 'selling', 'renting', 'lending', 'licensing'];

  return {
    searchTerm,
    sortBy,
    filters: {
      ...filters,
      roleTab: validRoleTabs.includes(roleTab) ? roleTab : 'all',
      status: validStatuses.includes(status) ? status : 'all',
      orderType,
      dateFrom: dateFrom ? new Date(dateFrom).toISOString().split('T')[0] : null,
      dateTo: dateTo ? new Date(dateTo).toISOString().split('T')[0] : null,
      minAmount: minAmount ? parseFloat(minAmount) : null,
      maxAmount: maxAmount ? parseFloat(maxAmount) : null,
      category,
      brand
    }
  };
};

// Update URL with current filters
const updateTransactionUrl = (searchTerm, sortBy, filters) => {
  const params = new URLSearchParams();

  if (searchTerm) params.set('search', searchTerm);
  if (sortBy !== 'orders') params.set('sort', sortBy);
  if (filters.roleTab !== 'all') params.set('role', filters.roleTab);
  if (filters.status !== 'all') params.set('status', filters.status);
  if (filters.orderType !== 'all') params.set('type', filters.orderType);
  if (filters.dateFrom) params.set('from', filters.dateFrom);
  if (filters.dateTo) params.set('to', filters.dateTo);
  if (filters.minAmount) params.set('minAmount', filters.minAmount);
  if (filters.maxAmount) params.set('maxAmount', filters.maxAmount);
  if (filters.category) params.set('category', filters.category);
  if (filters.brand) params.set('brand', filters.brand);

  const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
  window.history.replaceState({}, '', newUrl);

};

// Helper to calculate filter counts from search results
const calculateFilterCounts = (orders) => {
  const counts = {
    ongoing: 0,
    completed: 0,
    canceled: 0,
    buying: 0,
    selling: 0,
    renting: 0,
    lending: 0
  };

  orders.forEach(order => {
    // Count by display status
    if (order.displayStatus === 'ongoing') counts.ongoing++;
    else if (order.displayStatus === 'completed') counts.completed++;
    else if (order.displayStatus === 'canceled') counts.canceled++;

    // Count by transaction types (check order items)
    if (order.orderItems) {
      const hasBuyItems = order.orderItems.some(item => item.type === 'buy');
      const hasRentItems = order.orderItems.some(item => item.type === 'rent');

      if (hasBuyItems) {
        counts.buying++;
        counts.selling++;
      }
      if (hasRentItems) {
        counts.renting++;
        counts.lending++;
      }
    }
  });

  return counts;
};

// Async thunk for searching orders
export const searchOrdersAsync = createAsyncThunk(
  'algoliaOrders/searchOrders',
  async ({ searchTerm, filters, sortBy, offset = 0, limit = 5, loadMore = false }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const userId = state.auth?.user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }


      const result = await searchOrders({
        searchTerm,
        filters,
        sortBy,
        offset,
        limit,
        userId
      }, () => state);

      return { ...result, loadMore, offset };
    } catch (error) {
      console.error('Error searching orders:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for loading more orders
export const loadMoreOrdersAsync = createAsyncThunk(
  'algoliaOrders/loadMoreOrders',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const { searchTerm, filters, sortBy, orders, hasMore } = state.algoliaOrders;

    if (!hasMore) return { orders: [], hasMore: false };

    const offset = orders.length;

    return dispatch(searchOrdersAsync({
      searchTerm,
      filters,
      sortBy,
      offset,
      limit: 5,
      loadMore: true
    })).unwrap();
  }
);


const algoliaOrderSlice = createSlice({
  name: 'algoliaOrders',
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },

    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },

    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    setRoleTab: (state, action) => {
      const newRoleTab = action.payload;
      state.filters.roleTab = newRoleTab;

      // Clear incompatible filters when switching role tabs
      if (['buying', 'renting'].includes(newRoleTab)) {
        // Customer perspective - clear merchant-specific filters if any
      } else if (['selling', 'lending'].includes(newRoleTab)) {
        // Merchant perspective - clear customer-specific filters if any
      }
    },

    setStatusFilter: (state, action) => {
      const newStatus = action.payload;
      state.filters.status = newStatus;
    },

    clearFilters: (state) => {
      const clearedFilters = { ...initialState.filters };
      state.filters = clearedFilters;
      state.searchTerm = '';
    },

    initializeFromUrl: (state) => {
      const urlParams = parseTransactionUrlParams();
      state.searchTerm = urlParams.searchTerm;
      state.sortBy = urlParams.sortBy;
      state.filters = urlParams.filters;
      state.isInitialized = true;
    },

    updateUrl: (state) => {
      updateTransactionUrl(state.searchTerm, state.sortBy, state.filters);
    },

    clearOrders: (state) => {
      state.orders = [];
      state.totalResults = 0;
      state.currentPage = 0;
      state.totalPages = 0;
      state.hasMore = false;
      state.filterCounts = { ...initialState.filterCounts };
    },

    setSearchSuggestions: (state, action) => {
      state.searchSuggestions = action.payload;
    },

    setSuggestionsLoading: (state, action) => {
      state.suggestionsLoading = action.payload;
    },

    // New action to update filter counts
    updateFilterCounts: (state, action) => {
      state.filterCounts = action.payload;
    },


  },

  extraReducers: (builder) => {
    builder
      // Search orders
      .addCase(searchOrdersAsync.pending, (state, action) => {
        const { loadMore } = action.meta.arg;
        if (!loadMore) {
          state.loading = true;
          state.error = null;
        }
      })
      .addCase(searchOrdersAsync.fulfilled, (state, action) => {
        const { orders, totalResults, currentPage, totalPages, hasMore, loadMore } = action.payload;

        state.loading = false;
        state.error = null;

        if (loadMore) {
          state.orders = [...state.orders, ...orders];
        } else {
          state.orders = orders;
        }

        state.totalResults = totalResults;
        state.currentPage = currentPage;
        state.totalPages = totalPages;
        state.hasMore = hasMore;

        // Calculate filter counts for UI badges
        state.filterCounts = calculateFilterCounts(state.orders);

      })
      .addCase(searchOrdersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to search orders';
        console.error('Order search failed:', action.payload);
      })

      // Load more orders — searchOrdersAsync.fulfilled already appends when loadMore=true,
      // so we only update loading state here to avoid double-appending.
      .addCase(loadMoreOrdersAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadMoreOrdersAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loadMoreOrdersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load more orders';
        console.error('Load more orders failed:', action.payload);
      })

      // Matcher for order creation
      .addMatcher(
        orderApi.endpoints.createOrder.matchFulfilled,
        (state) => {
          // Order created — transactions will refresh on next visit
        }
      );
  }
});

export const {
  setSearchTerm,
  setSortBy,
  setFilters,
  setRoleTab,
  setStatusFilter,
  clearFilters,
  initializeFromUrl,
  updateUrl,
  clearOrders,
  setSearchSuggestions,
  setSuggestionsLoading,
  updateFilterCounts
} = algoliaOrderSlice.actions;

export default algoliaOrderSlice.reducer; 